import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  fetchAnalyzerSessions,
  fetchAnalyzerExchangesBulk,
  fetchResultsJson,
  fetchSessionResponses,
  deleteAnalyzerSession,
  getSurveySummaryFromCloud,
  getSurveyVersionFromCloud,
  listDeploymentUsersFromCloud,
} from '../utils/cloudApi';

/**
 * SRA — Session Results Analyzer.
 *
 * The Console's analysis engine over executed surveys: sessions, per-session
 * exchange timelines, AI prompts and NAP (next-action-plan) data, and the
 * deployment-level results view. Extracted from App.js as a discrete module
 * (refactor Step 3) because it is an essential part of the Explorer story and
 * is expected to grow; all state and effects below were moved verbatim.
 *
 * Some state (promptsByExchange, napByExchange, exchangesBySessionId,
 * expandedSessionIds, consistencyWarning, sraUnavailable, resultsAt,
 * resultsLoading, resultsError, resultsMode) is populated by live effects but
 * not currently rendered anywhere — the panel that displayed it was removed.
 * It is kept deliberately as the working surface for the expanded SRA UI.
 *
 * Params:
 *  - selectedDeploymentId, selectedSurveyId: current selection (App-owned).
 *  - deployments: the deployments list (App-owned; used to resolve survey ids
 *    and to eager-fetch session counts).
 *  - lastSnapshotRef: App's offline-snapshot ref; session lists are written
 *    into snapshot.sessionsBySurveyId for offline viewing and cached counts.
 *  - onError: (message) => void — analyzer-unavailable and load errors.
 *
 * Returns the full SRA state surface plus:
 *  - participantEmailMap: Map of participant/recipient id → email.
 *  - deleteSessions(sessionIds): deletes the given sessions server-side
 *    (continuing past individual failures, as before) and prunes local state.
 *    Confirmation UI stays with the caller.
 */
export default function useSRA({ selectedDeploymentId, selectedSurveyId, deployments, lastSnapshotRef, onError }) {
  const [sessions, setSessions] = useState([]);
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [exchanges, setExchanges] = useState([]);
  const [promptsByExchange, setPromptsByExchange] = useState({});
  const [napByExchange, setNapByExchange] = useState({});
  const [promptModal, setPromptModal] = useState(null); // { exchange_seq, prompt, wrap }
  const [napModal, setNapModal] = useState(null); // { exchange_seq, nap, showJson }
  const [dataFreshness, setDataFreshness] = useState({ sessionsAt: null, exchangesAt: null });
  const [consistencyWarning, setConsistencyWarning] = useState('');
  const [sraUnavailable, setSraUnavailable] = useState(false);
  const [selectedSessionIds, setSelectedSessionIds] = useState(new Set());
  const [exchangesBySessionId, setExchangesBySessionId] = useState({});
  const [expandedSessionIds, setExpandedSessionIds] = useState(new Set());
  const [variableByQuestionId, setVariableByQuestionId] = useState({});
  const [deploymentUsersForSra, setDeploymentUsersForSra] = useState([]);
  // Results view state (Pass 3)
  const [resultsMode, setResultsMode] = useState('timeline'); // 'timeline' | 'results'
  const [resultsRows, setResultsRows] = useState([]);
  const [resultsAt, setResultsAt] = useState(null);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [resultsError, setResultsError] = useState(null);
  const [csvFormat, setCsvFormat] = useState('long'); // 'long' | 'wide'
  const [isSraVisible, setIsSraVisible] = useState(false);
  // Phase 18: debouncing support
  const sessionsDebounceRef = useRef(null);
  const exchangesDebounceRef = useRef(null);

  // Mark SRA visible on first render so sessions can lazy-load
  useEffect(() => { setIsSraVisible(true); }, []);

  // Enable SRA when a deployment is selected (lazy load trigger)
  useEffect(() => {
    if (selectedDeploymentId) {
      setIsSraVisible(true);
    }
  }, [selectedDeploymentId]);

  // Defect #3c: Eager-fetch session counts for all deployed surveys on load
  useEffect(() => {
    if (!deployments || deployments.length === 0) return;
    const openDeps = deployments.filter(d => d.is_open === true || d.status === 'open' || d.status === 'active');
    const surveyIds = Array.from(new Set(openDeps.map(d => d.survey_id).filter(Boolean)));
    if (surveyIds.length === 0) return;

    let cancelled = false;
    (async () => {
      const snap = lastSnapshotRef.current || {};
      snap.sessionsBySurveyId = snap.sessionsBySurveyId || {};
      // Only fetch surveys not already cached
      const uncached = surveyIds.filter(id => !snap.sessionsBySurveyId[String(id)] || snap.sessionsBySurveyId[String(id)].length === 0);
      if (uncached.length === 0) return;
      // Batch fetch in parallel, max 5 concurrent
      const BATCH = 5;
      for (let i = 0; i < uncached.length; i += BATCH) {
        if (cancelled) return;
        const batch = uncached.slice(i, i + BATCH);
        const results = await Promise.allSettled(
          batch.map(sid => fetchAnalyzerSessions(sid, { limit: 50, offset: 0 }))
        );
        results.forEach((r, idx) => {
          if (r.status === 'fulfilled') {
            const sessions = (r.value || []).filter(s => !s.survey_id || String(s.survey_id) === String(batch[idx]));
            snap.sessionsBySurveyId[String(batch[idx])] = sessions;
          }
        });
      }
      if (!cancelled) {
        lastSnapshotRef.current = snap;
        try { sessionStorage.setItem('console_last_snapshot', JSON.stringify(snap)); } catch (_) { }
        // Trigger re-render by bumping a state
        setDataFreshness(prev => ({ ...prev, eagerSessionsAt: Date.now() }));
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deployments]);

  // Load results JSON when deployment changes or when switching to results mode
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (resultsMode !== 'results' || !selectedDeploymentId) { setResultsRows([]); setResultsError(null); return; }
        setResultsLoading(true);
        setResultsError(null);

        // If sessions are selected, fetch responses per session
        const selectedIds = Array.from(selectedSessionIds);
        if (selectedIds.length > 0) {
          const allResponses = [];
          for (const sid of selectedIds) {
            try {
              const rows = await fetchSessionResponses(sid, { timeoutMs: 8000 });
              if (cancelled) return;
              if (Array.isArray(rows)) {
                // Normalize session response fields to match Results table columns
                const sessionObj = (sessions || []).find(s => s.session_id === sid);
                const recipientId = sessionObj ? (sessionObj.participant_id || sessionObj.responder_id || sid) : sid;
                rows.forEach(r => {
                  r._session_id = sid;
                  // Map session response fields → Results table fields
                  if (!r.recipient_id) r.recipient_id = recipientId;
                  if (!r.variable_name) r.variable_name = r.question_id || '';
                  if (r.value_json === undefined) r.value_json = r.response_text || r.response_value || '';
                  if (!r.created_at) r.created_at = r.timestamp || '';
                });
                allResponses.push(...rows);
              }
            } catch (err) { console.warn(`[SRA] Failed to fetch responses for session ${sid}:`, err?.message || err); }
          }
          if (cancelled) return;
          if (allResponses.length > 0) {
            setResultsRows(allResponses);
          } else {
            // Session responses empty (legacy/presentor sessions) — fall back to deployment results
            // but filter by the selected sessions' participant IDs so we only show relevant results
            const rows = await fetchResultsJson(selectedDeploymentId, { timeoutMs: 8000 });
            if (cancelled) return;
            const allRows = Array.isArray(rows) ? rows : [];
            // Collect participant_ids for selected sessions
            const participantIds = new Set();
            for (const sid of selectedIds) {
              const sessionObj = (sessions || []).find(s => s.session_id === sid);
              if (sessionObj) {
                const pid = sessionObj.participant_id || sessionObj.responder_id;
                if (pid) participantIds.add(pid);
              }
            }
            // If we have participant IDs, filter results to only those recipients
            if (participantIds.size > 0) {
              const filtered = allRows.filter(r => participantIds.has(r.recipient_id));
              setResultsRows(filtered);
            } else {
              setResultsRows(allRows);
            }
          }
        } else {
          // No sessions selected: fall back to deployment-level results
          const rows = await fetchResultsJson(selectedDeploymentId, { timeoutMs: 8000 });
          if (cancelled) return;
          setResultsRows(Array.isArray(rows) ? rows : []);
        }
        setResultsAt(Date.now());
      } catch (e) {
        if (cancelled) return;
        setResultsError(e && e.message ? e.message : 'Failed to load results');
        setResultsRows([]);
      } finally {
        if (!cancelled) setResultsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [resultsMode, selectedDeploymentId, selectedSessionIds, sessions]);

  // Load survey descriptor mapping (question_id → variable_name) for highlighting expected keys in NAP
  useEffect(() => {
    (async () => {
      try {
        if (!selectedSurveyId) { setVariableByQuestionId({}); return; }
        const summary = await getSurveySummaryFromCloud(selectedSurveyId);
        const ver = (summary && summary.latestVersion && summary.latestVersion.version_number) || 1;
        const { data } = await getSurveyVersionFromCloud(selectedSurveyId, ver);
        const payload = (data && (data.designer_payload_json || data.designerPayload || data.payload)) || null;
        const nodes = (payload && Array.isArray(payload.nodes)) ? payload.nodes : [];
        const map = {};
        nodes.forEach(n => {
          try {
            const isQuestion = (n && ((n.type === 'question') || (n.data && n.data.type === 'question')));
            if (!isQuestion) return;
            const id = n.id || n.question_id || (n.data && (n.data.id || n.data.question_id));
            const variable = n && n.data && (n.data.variable || n.data.variable_name);
            if (id && variable) map[String(id)] = String(variable);
          } catch (_) { }
        });
        setVariableByQuestionId(map);
      } catch (_) {
        setVariableByQuestionId({});
      }
    })();
  }, [selectedSurveyId]);

  // Load sessions when needed (debounced, lazy: only when SRA visible)
  useEffect(() => {
    if (sessionsDebounceRef.current) clearTimeout(sessionsDebounceRef.current);
    sessionsDebounceRef.current = setTimeout(async () => {
      try {
        if (!isSraVisible) return; // lazy: only load when SRA in view
        if (!selectedDeploymentId) { setSessions([]); if (process.env.NODE_ENV !== 'production') { try { console.info('[Analyzer]', 'sessions load blocked: no deployment selected'); } catch (_) { } } return; }
        // Resolve survey_id from selected deployment
        const dep = (deployments || []).find(d => d.deployment_id === selectedDeploymentId);
        const surveyId = dep && dep.survey_id;
        if (!surveyId) { setSessions([]); return; }
        const rows = await fetchAnalyzerSessions(surveyId, { limit: 50, offset: 0 });
        const filtered = (rows || []).filter(r => !r.survey_id || String(r.survey_id) === String(surveyId));
        setSessions(filtered);
        setDataFreshness(prev => ({ ...prev, sessionsAt: Date.now() }));
        if ((rows || []).length !== filtered.length) {
          setConsistencyWarning('Inconsistent data detected: some sessions did not match the selected survey and were hidden.');
        } else {
          setConsistencyWarning('');
        }
        if (process.env.NODE_ENV !== 'production') { try { console.info('[Analyzer]', 'sessions loaded', { count: (rows || []).length }); } catch (_) { } }
        setSelectedSessionId(null);
        setExchanges([]);
        // Update cached sessions
        try {
          const snap = lastSnapshotRef.current || JSON.parse(sessionStorage.getItem('console_last_snapshot') || 'null') || {};
          snap.sessionsBySurveyId = snap.sessionsBySurveyId || {};
          snap.sessionsBySurveyId[String(surveyId)] = filtered;
          lastSnapshotRef.current = snap;
          sessionStorage.setItem('console_last_snapshot', JSON.stringify(snap));
        } catch (_) { }
        setSraUnavailable(false);
      } catch (err) { setSessions([]); setSraUnavailable(true); onError(String(((err && err.message) || 'Analyzer unavailable'))); }
    }, 200);
    return () => { if (sessionsDebounceRef.current) clearTimeout(sessionsDebounceRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSraVisible, selectedDeploymentId, deployments]);

  // Load deployment users for SRA email lookup
  useEffect(() => {
    if (!selectedDeploymentId) { setDeploymentUsersForSra([]); return; }
    (async () => {
      try {
        const result = await listDeploymentUsersFromCloud(selectedDeploymentId);
        setDeploymentUsersForSra(result.items || result || []);
      } catch (_) { setDeploymentUsersForSra([]); }
    })();
  }, [selectedDeploymentId]);

  // Build participant_id / recipient_id → email lookup from deployment users + results
  const participantEmailMap = useMemo(() => {
    const map = new Map();
    for (const du of deploymentUsersForSra) {
      if (du.recipient_id && du.email) map.set(du.recipient_id, du.email);
      if (du.user_id && du.email) map.set(du.user_id, du.email);
    }
    // Also incorporate emails from session objects (backend now returns email via JOIN)
    for (const s of (sessions || [])) {
      if (s.participant_id && s.email && !map.has(s.participant_id)) {
        map.set(s.participant_id, s.email);
      }
    }
    // Also incorporate emails from results rows (backend now returns email field)
    for (const row of (resultsRows || [])) {
      if (row.recipient_id && row.email && !map.has(row.recipient_id)) {
        map.set(row.recipient_id, row.email);
      }
    }
    return map;
  }, [deploymentUsersForSra, sessions, resultsRows]);

  // Bug 9 fix: Clear stale session selection when deployment changes
  useEffect(() => {
    setSelectedSessionIds(new Set());
    setExchangesBySessionId({});
    setExpandedSessionIds(new Set());
    setPromptsByExchange({});
    setNapByExchange({});
    setExchanges([]);
    setSelectedSessionId(null);
  }, [selectedDeploymentId]);

  // Load exchanges when sessions are selected (debounced, bulk single-request)
  useEffect(() => {
    if (exchangesDebounceRef.current) clearTimeout(exchangesDebounceRef.current);

    const sessionIds = Array.from(selectedSessionIds ?? []);

    exchangesDebounceRef.current = setTimeout(async () => {
      try {
        // No sessions selected: clear state and exit
        if (sessionIds.length === 0) {
          setExchangesBySessionId({});
          setExchanges([]);
          setSelectedSessionId(null);
          if (process.env.NODE_ENV !== 'production') { try { console.info('[Analyzer]', 'exchanges cleared: no sessions selected'); } catch (_) { } }
          return;
        }

        // Single bulk request: 1 HTTP call, 1 DB query via ANY($1)
        // Returns { [sessionId]: { exchanges: [...], prompts: { seq: [...] } } }
        const bulkResult = await fetchAnalyzerExchangesBulk(sessionIds, { timeoutMs: 15000 });

        const byId = {};
        const allPrompts = {};
        const allNap = {};

        for (const sessionId of sessionIds) {
          const sessionData = bulkResult[sessionId] || { exchanges: [], prompts: {} };
          const rows = sessionData.exchanges || [];
          const inlinePrompts = sessionData.prompts || {};

          byId[sessionId] = sessionData;

          // Inline prompts from analyzer-events (kernel.2 ai_prompt events)
          for (const [seq, promptList] of Object.entries(inlinePrompts)) {
            const seqNum = Number(seq);
            if (!allPrompts[seqNum]) allPrompts[seqNum] = [];
            allPrompts[seqNum].push(...promptList);
          }
          // Extract NAP data from inline exchange ai_responses
          for (const exchange of rows) {
            const seq = exchange.exchange_seq;
            if (seq == null) continue;
            for (const resp of (exchange.ai_responses || [])) {
              if (resp.nextInstruction || resp.shouldAdvanceSurvey != null || resp.extractedData) {
                allNap[seq] = resp;
              }
            }
          }
        }

        setPromptsByExchange(allPrompts);
        setNapByExchange(allNap);

        setExchangesBySessionId(byId);
        setDataFreshness((prev) => ({ ...prev, exchangesAt: Date.now() }));

        // Auto-expand newly loaded sessions
        setExpandedSessionIds((prev) => {
          const next = new Set(prev);
          sessionIds.forEach((id) => next.add(id));
          return next;
        });

        // Backward compatibility: if only one session, update legacy single-session state
        if (sessionIds.length === 1) {
          const onlyId = sessionIds[0];
          setSelectedSessionId(onlyId);
          setExchanges((byId[onlyId] && byId[onlyId].exchanges) || []);
        } else {
          setExchanges([]); // keep single-session state clean when multi-select
          setSelectedSessionId(null);
        }

        if (process.env.NODE_ENV !== 'production') { try { console.info('[Analyzer]', 'exchanges loaded (bulk)', { count: sessionIds.length }); } catch (_) { } }

        setSraUnavailable(false);
      } catch (err) {
        setExchangesBySessionId({});
        setExchanges([]);
        setSraUnavailable(true);
        onError(String(((err && err.message) || 'Analyzer unavailable')));
      }
    }, 200);

    return () => { if (exchangesDebounceRef.current) clearTimeout(exchangesDebounceRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSessionIds]);

  // Selection change logging (dev-only)
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') { try { console.info('[Selection]', 'session selected', { sessionId: selectedSessionId }); } catch (_) { } }
  }, [selectedSessionId]);

  // Delete the given sessions (Set or array of ids), continuing past individual
  // failures, then prune local state. Confirmation and loading UI are the
  // caller's responsibility.
  const deleteSessions = useCallback(async (sessionIds) => {
    const ids = Array.from(sessionIds || []);
    if (ids.length === 0) return;
    const idSet = new Set(ids);
    for (const sid of ids) {
      try { await deleteAnalyzerSession(sid, { timeoutMs: 4000 }); } catch (e) { /* continue deleting others */ }
    }
    // Remove deleted sessions locally and clear selection
    setSessions(prev => (prev || []).filter(s => !idSet.has(s.session_id)));
    setSelectedSessionIds(new Set());
    setSelectedSessionId(null);
    setExchanges([]);
  }, []);

  return {
    // sessions and selection
    sessions,
    selectedSessionId,
    selectedSessionIds, setSelectedSessionIds,
    expandedSessionIds, setExpandedSessionIds,
    // exchange timelines and AI metadata
    exchanges,
    exchangesBySessionId,
    promptsByExchange,
    napByExchange,
    promptModal, setPromptModal,
    napModal, setNapModal,
    variableByQuestionId,
    // results view
    resultsMode, setResultsMode,
    resultsRows,
    resultsAt,
    resultsLoading,
    resultsError,
    csvFormat, setCsvFormat,
    // lookups and status
    participantEmailMap,
    dataFreshness,
    consistencyWarning,
    sraUnavailable,
    // actions
    deleteSessions,
  };
}
